/**
 * @flow
 */

import React, { useCallback } from 'react';
import gql from 'graphql-tag';
import { isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { Button, Loader } from 'semantic-ui-react';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroller';
import { Table, EllipsisLoader } from '../../../components';

import type { StripeInvoice, StripeInvoiceEdge, PageInfo } from '../../../type';

export const GET_INVOICES_QUERY = gql`
    query getInvoices($salon: ID!, $first: Int, $after: String) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    invoices(first: $first, after: $after) {
                        pageInfo {
                            endCursor
                            hasNextPage
                        }
                        edges {
                            cursor
                            node {
                                invoicePdf
                                total
                                status
                                number
                                periodStart
                                periodEnd
                            }
                        }
                    }
                }
            }
        }
    }
`;

type InvoiceRowProps = {
    invoice: StripeInvoice,
};

const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={3} textAlign="center">
            <FormattedMessage
                id="pages.administrators.InvoicesList.noInvoices"
                defaultMessage="You don't have any invoices yet."
            />
        </Table.Cell>
    </Table.Row>
);

const LoadMoreSpinner = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={3} textAlign="center">
            <EllipsisLoader size="1em" />
        </Table.Cell>
    </Table.Row>
);

const InvoiceRow = ({ invoice }: InvoiceRowProps) => {
    return (
        <Table.Row>
            <Table.Cell>{invoice.number}</Table.Cell>
            <Table.Cell>{(invoice.total / 100).toFixed(2)}</Table.Cell>
            <Table.Cell>
                <Button primary href={invoice.invoicePdf} target="_blank">
                    <FormattedMessage
                        id="pages.administrators.InvoicesList.download"
                        defaultMessage="Download"
                    />
                </Button>
            </Table.Cell>
        </Table.Row>
    );
};

const InvoicesList = () => {
    const salon = useSelector(({ user }) => user.get('salon'));

    const { data, loading, fetchMore } = useQuery(GET_INVOICES_QUERY, {
        variables: { salon, first: 20 },
        fetchPolicy: 'cache-and-network',
    });

    const invoices = data?.viewer?.salon?.invoices?.edges ?? [];
    const pageInfo = data?.viewer?.salon?.invoices?.pageInfo ?? {};

    const loadMore = useCallback(() => {
        if (loading || !pageInfo.hasNextPage) {
            return;
        }

        fetchMore({
            query: GET_INVOICES_QUERY,
            variables: {
                salon,
                first: 20,
                after: pageInfo.endCursor,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                const prevEdges = previousResult.viewer.salon.invoices.edges;
                const newEdges = fetchMoreResult.viewer.salon.invoices.edges;

                const newResults = Object.assign({}, fetchMoreResult);
                newResults.viewer.salon.invoices.edges = [
                    ...prevEdges,
                    ...newEdges,
                ];

                return newResults;
            },
        });
    }, [loading, pageInfo, salon, fetchMore]);

    if (loading && isEmpty(invoices)) {
        return <Loader size="big" inline="centered" active />;
    }

    return (
        <Table celled selectable striped fixed>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>
                        <FormattedMessage
                            id="pages.administrators.InvoicesList.number"
                            defaultMessage="Number"
                        />
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        <FormattedMessage
                            id="pages.administrators.InvoicesList.total"
                            defaultMessage="Total"
                        />
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        <FormattedMessage
                            id="pages.administrators.InvoicesList.actions"
                            defaultMessage="Actions"
                        />
                    </Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <InfiniteScroll
                element="tbody"
                pageStart={0}
                loadMore={loadMore}
                initialLoad={false}
                hasMore={pageInfo.hasNextPage}
                loader={<LoadMoreSpinner key={0} />}
                threshold={1}
                useWindow
            >
                {isEmpty(invoices) ? (
                    <EmptyMessage />
                ) : (
                    invoices.map(invoice => (
                        <InvoiceRow
                            key={invoice.cursor}
                            invoice={invoice.node}
                            pageInfo={pageInfo}
                        />
                    ))
                )}
            </InfiniteScroll>
        </Table>
    );
};

export default InvoicesList;
